<template>
  <div>
    <SectionContainer height="none" tag="div">
      <SBreadcrumbsSeo
        :breadcrumbs="[
          { label: 'Home', href: ziggy('mainSite.homepage') },
          {
            label: geoProductCategoryName,
            href: ziggy('client.slug', { slug: routeParams.geoProductSlug }),
          },
          { label: provinceName },
        ]"
      />
    </SectionContainer>

    <SectionContainer
      class="company-wall-province__hero-section"
      height="none"
      size="md"
    >
      <CompanyWallHero
        :benefits="provinceBenefits"
        :form-url="formUrl"
        :heading="tr('main_site.company_wall.province.page_title')"
      >
        <template #subtitle>
          <div class="company-wall-province__hero-stats">
            <span class="company-wall-province__stat">
              <strong class="company-wall-province__stat-value">
                <RoundedSpecialistCount
                  :count="geoProductCategory?.totalCustomers"
                />
              </strong>
              {{
                tr(
                  "main_site.company_wall.province.hero.stats.total_specialists",
                )
              }}
            </span>
            <span class="company-wall-province__stat">
              <i class="fas fa-star company-wall-province__star-icon" />
              <strong class="company-wall-province__stat-value">{{
                geoProductCategory?.averageRating
              }}</strong>
              {{
                tr("main_site.company_wall.province.hero.stats.average_score")
              }}
            </span>
            <span class="company-wall-province__stat">
              <strong class="company-wall-province__stat-value">{{
                municipalities?.length
              }}</strong>
              {{
                tr(
                  "main_site.company_wall.province.hero.stats.total_municipalities",
                )
              }}
            </span>
          </div>
        </template>
      </CompanyWallHero>
    </SectionContainer>

    <SectionContainer bg-color="light" size="md">
      <template v-if="municipalities?.length">
        <SHeading centered level="2" size="2">
          {{ municipalitiesTitle }}
        </SHeading>
        <p class="company-wall-province__link-description">
          {{ tr("main_site.company_wall.province.municipalities_description") }}
        </p>
        <SButtonList
          :items="municipalities"
          :max-items="12"
          prefix-icon="far fa-location-dot"
          render-all
          :show-more-label="
            tr('main_site.company_wall.province.show_more_label')
          "
          :tag="NuxtLink"
        />
      </template>

      <template v-else-if="municipalitiesStatus !== 'error'">
        <SHeadingSkeleton size="2" width="60%" />
        <STextSkeleton font-size="lg" width="25rem" />
        <SButtonListSkeleton />
      </template>
    </SectionContainer>

    <SectionContainer bg-color="light" size="md">
      <template v-if="provinces?.length">
        <SHeading centered level="2" size="2">
          {{ provincesTitle }}
        </SHeading>
        <p class="company-wall-province__link-description">
          {{ tr("main_site.company_wall.province.provinces_description") }}
        </p>
        <SButtonList
          :items="provinces"
          :max-items="12"
          render-all
          :show-more-label="
            tr('main_site.company_wall.province.show_more_label')
          "
          :tag="NuxtLink"
        />
      </template>

      <template v-else-if="provincesStatus !== 'error'">
        <SHeadingSkeleton size="2" width="60%" />
        <STextSkeleton font-size="lg" width="25rem" />
        <SButtonListSkeleton />
      </template>
    </SectionContainer>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { useRoute } from "#app";
import { NuxtLink } from "#components";
import { pick } from "radash";
import { computed } from "vue";

import {
  SButtonList,
  SButtonListSkeleton,
  SHeading,
  SHeadingSkeleton,
  STextSkeleton,
} from "@solvari/components";
import {
  injectI18nLocale,
  localeToIso,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import { useZiggy, ziggy } from "@/shared/libs/ziggy";
import {
  useMunicipalities,
  useProvinces,
} from "~/features/company-wall/api/geo.api.ts";
import { useCmsLocalSearch } from "~/features/company-wall/api/localSearchCms.api.ts";
import CompanyWallHero from "~/features/company-wall/components/company-wall-hero/CompanyWallHero.vue";
import RoundedSpecialistCount from "~/features/company-wall/components/company-wall-hero/RoundedSpecialistCount.vue";
import { useLocalSearchData } from "~/features/company-wall/composables/useLocalSearch";
import SBreadcrumbsSeo from "~/shared/components/SBreadcrumbsSeo.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { useHost } from "~/shared/composables/useHost.ts";
import {
  useSeoNormalized,
  useStructuredData,
} from "~/shared/composables/useMetaData.ts";
import { usePageContext } from "~/shared/composables/usePageContext.ts";

import {
  metaDescriptionFallback,
  metaTitleFallback,
  seoWithCmsFallback,
  useLocalSearchTranslations,
} from "../composables/useLocalSearchTranslations.ts";

const routeParams = pick(useRoute().params, ["geoProductSlug", "province"]) as {
  geoProductSlug: string;
  province: string;
};

const localeCode = injectI18nLocale().value;

const host = useHost();
const headZiggy = useZiggy();

usePageContext({ pageType: "local_search_province" });

const { geoProductCategory, province } = useLocalSearchData({
  ...routeParams,
  localeCode,
});

const geoProductCategoryName = computed(
  () => geoProductCategory.value?.name ?? routeParams.geoProductSlug,
);

const formUrl = ziggy("mainSite.geoProduct.leadForm", {
  geoProductSlug: routeParams.geoProductSlug,
  formSlug: undefined,
});

const provinceName = computed(
  () => province.value?.name ?? routeParams.province,
);

const { tr, interpolateTranslation } = useLocalSearchTranslations(
  useI18nMainSiteCompanyWall,
  {
    ...routeParams,
    localeCode,
  },
);

const { data: cmsLocalSearch } = useQuery(
  useCmsLocalSearch({
    uri: `/${routeParams.geoProductSlug}`,
    locale: localeCode,
  }),
);

const provinceBenefits = computed(() => [
  tr("main_site.company_wall.province.hero.benefits.benefit_1"),
  tr("main_site.company_wall.province.hero.benefits.benefit_2"),
  tr("main_site.company_wall.province.hero.benefits.benefit_3"),
]);

const { data: municipalitiesData, status: municipalitiesStatus } =
  useMunicipalities({
    provinceSlug: routeParams.province,
    localeCode,
  });

const municipalities = computed(() =>
  municipalitiesData.value?.map((item) => ({
    ...item,
    to: ziggy("mainSite.localSearch.municipality", {
      geoProductSlug: routeParams.geoProductSlug,
      province: routeParams.province,
      municipality: item.slug,
    }),
  })),
);

const { data: provincesData, status: provincesStatus } = useProvinces({
  localeCode,
});

const provinces = computed(() =>
  provincesData.value?.map((item) => ({
    ...item,
    to: ziggy("mainSite.localSearch.province", {
      geoProductSlug: routeParams.geoProductSlug,
      province: item.slug,
    }),
  })),
);

const metaTitle = computed(() =>
  seoWithCmsFallback(
    cmsLocalSearch.value?.province.seoTitles,
    () => [
      tr("main_site.company_wall.province.seo_title_1"),
      tr("main_site.company_wall.province.seo_title_2"),
      tr("main_site.company_wall.province.seo_title_3"),
    ],
    interpolateTranslation,
    metaTitleFallback,
  ),
);

const municipalitiesTitle = computed(() =>
  tr("main_site.company_wall.province.municipalities_title"),
);

const provincesTitle = computed(() =>
  tr("main_site.company_wall.province.provinces_title"),
);

const metaDescription = computed(() =>
  seoWithCmsFallback(
    cmsLocalSearch.value?.province.seoDescriptions,
    () => [
      tr("main_site.company_wall.province.seo_description_1"),
      tr("main_site.company_wall.province.seo_description_2"),
      tr("main_site.company_wall.province.seo_description_3"),
    ],
    interpolateTranslation,
    metaDescriptionFallback,
  ),
);

useSeoNormalized({
  title: metaTitle,
  description: metaDescription,
  index: () => geoProductCategory.value?.localSearchIndexable ?? true,
});

const customerPlural = computed(
  () => cmsLocalSearch.value?.customerPlural ?? geoProductCategoryName.value,
);

const pageUrl = `https://${host}${headZiggy("mainSite.localSearch.province", {
  geoProductSlug: routeParams.geoProductSlug,
  province: routeParams.province,
})}`;

useStructuredData(() => {
  const municipalityList = municipalitiesData.value;
  if (!municipalityList?.length) {
    return undefined;
  }
  return {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    "@type": "CollectionPage",
    name: `${customerPlural.value} in ${provinceName.value}`,
    description: metaDescription.value,
    url: pageUrl,
    inLanguage: localeToIso(localeCode),
    mainEntity: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "@type": "ItemList",
      name: tr("main_site.company_wall.province.structured_data_list_name"),
      numberOfItems: municipalityList.length,
      itemListElement: municipalityList.map((municipality, index) => ({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "@type": "ListItem",
        position: index + 1,
        name: `${customerPlural.value} in ${municipality.name}`,
        url: `https://${host}${headZiggy("mainSite.localSearch.municipality", {
          geoProductSlug: routeParams.geoProductSlug,
          province: routeParams.province,
          municipality: municipality.slug,
        })}`,
      })),
    },
  };
});
</script>

<style lang="postcss">
.company-wall-province {
  &__hero-section {
    @apply mb-8 mt-2;

    @screen 1000 {
      @apply mb-12 mt-4;
    }
  }

  &__link-description {
    @apply mb-6 text-center text-lg text-slate-700;
  }

  &__form-placeholder {
    @apply flex h-20 items-center justify-center text-slate-400;
  }

  &__hero-stats {
    @apply flex flex-wrap items-center justify-center gap-4;
  }

  &__stat {
    @apply flex items-center text-sm font-light text-slate-500 gap-1;
  }

  &__stat-value {
    @apply font-bold text-primary-700;
  }

  &__star-icon {
    @apply mb-0.5 text-xs text-primary-700;
  }
}
</style>
